// Volumetric clouds (after Schneider, "Nubis"): a tileable Perlin-Worley 3D
// texture baked on the GPU, a coverage/height-gradient density model, and a
// raymarch between two spherical shells with Beer-powder lighting and a
// multiple-scattering approximation.

export const NOISE3D_FRAG = /* glsl */ `
precision highp float;
uniform float uZ;
in vec2 vUv;

vec3 hash33(vec3 p) {
  p = fract(p * vec3(0.1031, 0.1030, 0.0973));
  p += dot(p, p.yxz + 33.33);
  return fract((p.xxy + p.yxx) * p.zyx);
}

float worley(vec3 uvw, float freq) {
  vec3 p = uvw * freq;
  vec3 id = floor(p);
  vec3 f = fract(p);
  float md = 1.0;
  for (int x = -1; x <= 1; x++)
  for (int y = -1; y <= 1; y++)
  for (int z = -1; z <= 1; z++) {
    vec3 o = vec3(float(x), float(y), float(z));
    vec3 cid = mod(id + o, freq);
    vec3 d = hash33(cid) + o - f;
    md = min(md, dot(d, d));
  }
  return 1.0 - sqrt(md);
}

vec3 grad(vec3 cell, float freq) {
  return normalize(hash33(mod(cell, freq)) * 2.0 - 1.0);
}

float perlin(vec3 uvw, float freq) {
  vec3 p = uvw * freq;
  vec3 i = floor(p);
  vec3 f = fract(p);
  vec3 u = f * f * f * (f * (f * 6.0 - 15.0) + 10.0);
  float n000 = dot(grad(i, freq), f);
  float n100 = dot(grad(i + vec3(1, 0, 0), freq), f - vec3(1, 0, 0));
  float n010 = dot(grad(i + vec3(0, 1, 0), freq), f - vec3(0, 1, 0));
  float n110 = dot(grad(i + vec3(1, 1, 0), freq), f - vec3(1, 1, 0));
  float n001 = dot(grad(i + vec3(0, 0, 1), freq), f - vec3(0, 0, 1));
  float n101 = dot(grad(i + vec3(1, 0, 1), freq), f - vec3(1, 0, 1));
  float n011 = dot(grad(i + vec3(0, 1, 1), freq), f - vec3(0, 1, 1));
  float n111 = dot(grad(i + vec3(1, 1, 1), freq), f - vec3(1, 1, 1));
  return mix(mix(mix(n000, n100, u.x), mix(n010, n110, u.x), u.y), mix(mix(n001, n101, u.x), mix(n011, n111, u.x), u.y), u.z);
}

float perlinFbm(vec3 uvw, float freq) {
  float s = 0.0, a = 1.0, n = 0.0;
  for (int o = 0; o < 5; o++) {
    s += perlin(uvw, freq) * a;
    n += a;
    a *= 0.5;
    freq *= 2.0;
  }
  return s / n * 0.5 + 0.5;
}

float worleyFbm(vec3 uvw, float freq) {
  return worley(uvw, freq) * 0.625 + worley(uvw, freq * 2.0) * 0.25 + worley(uvw, freq * 4.0) * 0.125;
}

float remap(float v, float a, float b, float c, float d) {
  return c + (v - a) / (b - a) * (d - c);
}

void main() {
  vec3 uvw = vec3(vUv, uZ);
  float pf = perlinFbm(uvw, 4.0);
  float wf = worleyFbm(uvw, 4.0);
  float pw = clamp(remap(pf, 0.0, 1.0, wf, 1.0), 0.0, 1.0);
  gl_FragColor = vec4(pw, wf, worleyFbm(uvw, 8.0), worleyFbm(uvw, 16.0));
}
`;

/** Density model + raymarch. Requires ATMOSPHERE_COMMON and uTrans. */
export const CLOUDS_GLSL = /* glsl */ `
uniform sampler3D uNoise;
uniform vec4 uCloud;        // coverage, density, base (m), thickness (m)
uniform float uStratus;
uniform vec3 uCloudOffset;  // wind drift (m)
uniform int uCloudSteps;
uniform vec3 uKeyDir;       // sun or moon
uniform vec3 uKeyColor;     // its irradiance above the atmosphere (scene units)
uniform vec3 uAmbTop;
uniform vec3 uAmbBottom;
uniform vec4 uFlashPos;     // xyz world, w intensity
uniform float uFrame;

#define EARTH_M 6360000.0

float cRemap(float v, float a, float b, float c, float d) {
  return c + (v - a) / (b - a) * (d - c);
}

float heightGradient(float h, float cov) {
  float bottom = smoothstep(0.0, 0.08 + 0.1 * (1.0 - cov), h);
  float top = 1.0 - smoothstep(0.35 + 0.45 * cov, 1.0, h);
  return bottom * top;
}

float cloudDensity(vec3 wp, float h, bool detail) {
  vec3 p = wp + uCloudOffset;
  vec3 q = vec3(p.x, h * uCloud.w * 0.7 + uCloudOffset.y, p.z) / 7500.0;
  vec4 lf = texture(uNoise, q);
  // Normalise the Perlin-Worley and Worley fBm to roughly uniform 0..1
  // (calibrated against the baked texture's 5th..95th percentiles).
  float pw = clamp((lf.r - 0.62) / 0.23, 0.0, 1.0);
  float wf = clamp((lf.g * 0.625 + lf.b * 0.25 + lf.a * 0.125 - 0.3) / 0.36, 0.0, 1.0);
  float shape = pw * 0.7 + wf * 0.3;
  // Large-scale weather: clusters and clear patches tens of km across.
  float wm = clamp((texture(uNoise, vec3(p.xz / 52000.0, 0.37)).r - 0.62) / 0.23, 0.0, 1.0);
  float cov = clamp(uCloud.x + (wm - 0.5) * 0.4 * (1.0 - uCloud.x * 0.5), 0.0, 1.0);
  float g = heightGradient(h, cov);
  float d = clamp((shape * g - (1.0 - cov)) * 3.2, 0.0, 1.0);
  float st = uStratus * smoothstep(0.0, 0.06, h) * (1.0 - smoothstep(0.12, 0.42, h)) * (0.55 + 0.45 * pw);
  d = max(d, st * 0.6);
  if (d <= 0.0) return 0.0;
  if (detail) {
    // Erode edges with fine Worley: billows at the base, wisps at the top.
    vec3 hf = texture(uNoise, p / 1700.0 + vec3(0.0, h * 0.4, 0.0)).gba;
    float hfbm = clamp((dot(hf, vec3(0.625, 0.25, 0.125)) - 0.3) / 0.36, 0.0, 1.0);
    float hmod = mix(hfbm, 1.0 - hfbm, clamp(h * 4.0, 0.0, 1.0));
    d = clamp((d - hmod * 0.35) / (1.0 - hmod * 0.35), 0.0, 1.0);
  }
  return d * uCloud.y;
}

vec2 shellHits(vec3 ro, vec3 rd, float r0, float r1) {
  // ro relative to planet centre. Camera assumed below the cloud base.
  float t0 = raySphereFar(ro, rd, r0);
  float t1 = raySphereFar(ro, rd, r1);
  return vec2(t0, t1);
}

float igNoise(vec2 p) {
  return fract(52.9829189 * fract(dot(p, vec2(0.06711056, 0.00583715))));
}

// March clouds along rd from camera position cam (world, m). Returns colour
// (premultiplied) and transmittance.
vec4 marchClouds(vec3 cam, vec3 rd, vec2 pixel, out float hitDist) {
  hitDist = 1e9;
  if (uCloud.y <= 0.0 || (uCloud.x <= 0.01 && uStratus <= 0.01)) return vec4(0.0, 0.0, 0.0, 1.0);
  vec3 ro = vec3(0.0, EARTH_M + cam.y, 0.0);
  float base = uCloud.z, thick = uCloud.w;
  vec2 hits = shellHits(ro, rd, EARTH_M + base, EARTH_M + base + thick);
  float tStart = hits.x;
  float tEnd = min(hits.y, tStart + 34000.0);
  if (tStart < 0.0 || tStart > 90000.0) return vec4(0.0, 0.0, 0.0, 1.0);
  int steps = uCloudSteps;
  float len = tEnd - tStart;
  float dt = len / float(steps);
  float jitter = fract(igNoise(pixel) + uFrame * 0.61803398);
  float t = tStart + dt * jitter;
  vec3 col = vec3(0.0);
  float T = 1.0;
  float mu = dot(rd, uKeyDir);
  float phase = mix(phaseHG(mu, 0.75), phaseHG(mu, -0.25), 0.35) + 0.08;
  // Sunlight reaching the cloud layer, reddened near the horizon.
  float hKm = (base + thick * 0.3) * 0.001;
  vec3 keyAtCloud = uKeyColor * texture(uTrans, transmittanceUV(hKm, uKeyDir.y)).rgb;
  float weightedT = 0.0;
  float weight = 0.0;
  for (int i = 0; i < 128; i++) {
    if (i >= steps || T < 0.01) break;
    vec3 pr = ro + rd * t;
    float r = length(pr);
    float h = (r - EARTH_M - base) / thick;
    vec3 wp = cam + rd * t;
    float d = cloudDensity(wp, h, true);
    if (d > 0.001) {
      // Light march toward the key light.
      float od = 0.0;
      float ls = thick * 0.08;
      vec3 lp = wp;
      for (int j = 0; j < 6; j++) {
        lp += uKeyDir * ls;
        float lh = h + (lp.y - wp.y) / thick;
        od += cloudDensity(lp, clamp(lh, 0.0, 1.0), j < 2) * ls;
        ls *= 1.6;
      }
      float sigma = d * 0.045;
      float odL = od * 0.045;
      // Multiple scattering: sum of attenuated octaves (Wrenninge).
      float beer = exp(-odL) + 0.5 * exp(-odL * 0.25) * 0.7 + 0.25 * exp(-odL * 0.0625) * 0.5;
      float powder = 1.0 - exp(-sigma * 120.0);
      vec3 amb = mix(uAmbBottom, uAmbTop, clamp(h, 0.0, 1.0)) * (0.6 + 0.4 * h);
      // Light scattered many times inside the cloud never quite dies out.
      vec3 ms = keyAtCloud * 0.035 * (0.35 + 0.65 * h);
      vec3 S = keyAtCloud * beer * phase * mix(1.0, powder, 0.55) * 1.2 + amb + ms;
      if (uFlashPos.w > 0.0) {
        float fd = length(wp - uFlashPos.xyz);
        S += vec3(0.75, 0.8, 1.0) * uFlashPos.w * 40.0 * exp(-fd / 2200.0);
      }
      S *= sigma;
      float sT = exp(-sigma * dt);
      col += T * (S - S * sT) / sigma;
      weightedT += t * T * (1.0 - sT);
      weight += T * (1.0 - sT);
      T *= sT;
    }
    t += dt;
  }
  if (weight > 0.0) hitDist = weightedT / weight;
  return vec4(col, T);
}
`;
